import { EmoMic, EmoNeutral } from "@assets"
import { Head, Link, router } from "@inertiajs/react"
import { Heart, Plus } from "lucide-react"
import { useEffect, useState } from "react"

import { AuthButton } from "@/components/auth-button"
import { Button } from "@/components/ui/button"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useFavorites } from "@/hooks/use-favorites"
import AppLayout from "@/layouts/app-layout"
import { interviewPath, interviewsNewPath, interviewsPath } from "@/routes"

interface InterviewItem {
  id: string | number
  source_id?: number
  type: "interview"
  created_at: string
  title: string
  interviewee_name?: string | null
  excerpt?: string
  cover_url?: string | null
  is_favorited?: boolean
  favorites_count?: number
}

interface Props {
  interviews: InterviewItem[]
  filters?: {
    filter?: string
    status?: string
    neighborhood_id?: string | number
    search?: string
    sort?: string
  }
  can_create: boolean
  can_admin?: boolean
  pagination?: {
    current_offset: number
    limit: number
    total: number
    has_more: boolean
  }
}

function InterviewCard({ interview }: { interview: InterviewItem }) {
  const {
    isFavorited: getStoreFavorite,
    setFavorite: setStoreFavorite,
    getFavoritesCount: getStoreCount,
    setFavoritesCount: setStoreCount,
  } = useFavorites()

  const interviewId = interview.source_id ?? Number(interview.id)

  // Get stored values from global store (may be undefined if not set)
  const storedFavorite = getStoreFavorite("interview", interviewId)
  const storedCount = getStoreCount("interview", interviewId)

  const [localFavorited, setLocalFavorited] = useState(
    storedFavorite ?? Boolean(interview.is_favorited),
  )
  const [localFavoritesCount, setLocalFavoritesCount] = useState(
    storedCount ?? interview.favorites_count ?? 0,
  )

  // Sync with store when it changes
  useEffect(() => {
    const currentStoreFavorite = getStoreFavorite("interview", interviewId)
    const currentStoreCount = getStoreCount("interview", interviewId)

    setLocalFavorited(currentStoreFavorite ?? Boolean(interview.is_favorited))
    setLocalFavoritesCount(currentStoreCount ?? interview.favorites_count ?? 0)
  }, [
    interviewId,
    interview.is_favorited,
    interview.favorites_count,
    getStoreFavorite,
    getStoreCount,
  ])

  const handleFavorite = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const newValue = !localFavorited
    const newCount = newValue
      ? localFavoritesCount + 1
      : Math.max(0, localFavoritesCount - 1)

    // Update local state
    setLocalFavorited(newValue)
    setLocalFavoritesCount(newCount)

    // Sync with global store
    setStoreFavorite("interview", interviewId, newValue)
    setStoreCount("interview", interviewId, newCount)

    router.post(
      `/entrevistas/${interviewId}/favorite`,
      {},
      {
        preserveScroll: true,
        preserveState: true,
      },
    )
  }

  return (
    <div className="group bg-card relative block overflow-hidden rounded-[10px] shadow-sm transition-shadow duration-200 hover:shadow-md">
      <Link href={interviewPath(interview.id)}>
        <div className="w-full overflow-hidden">
          <div className="relative h-[94px] w-full flex-shrink-0 overflow-hidden sm:h-56">
            {interview.cover_url ? (
              <img
                src={interview.cover_url}
                alt={interview.title}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="bg-muted flex h-full items-center justify-center text-5xl">
                🧑‍💼
              </div>
            )}
            {interview.interviewee_name && (
              <div className="absolute right-0 bottom-0 left-0 bg-gradient-to-t from-black/70 to-transparent pb-[2px] pl-[6px]">
                <span className="h-[18px] truncate text-[12px] leading-[14px] font-semibold tracking-[0.18px] text-white">
                  {interview.interviewee_name}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="px-3 pt-[5px] pb-[7px]">
          <h3 className="text-foreground group-hover:text-primary line-clamp-2 text-[12px] font-medium tracking-[0.18px] transition-colors">
            {interview.title}
          </h3>
        </div>
      </Link>
      <AuthButton
        variant="ghost"
        size="sm"
        className="bg-card/80 hover:bg-card absolute top-2 right-2 h-auto rounded-full p-1"
        onClick={handleFavorite}
      >
        <Heart
          className={`h-4 w-4 ${localFavorited ? "text-destructive fill-current" : "text-muted-foreground"}`}
        />
        {localFavoritesCount > 0 && (
          <span className="ml-1 text-xs">{localFavoritesCount}</span>
        )}
      </AuthButton>
    </div>
  )
}

export default function InterviewsIndex({
  interviews = [],
  filters = {},
  can_create,
  can_admin = false,
  pagination,
}: Props) {
  const currentStatus = String(
    (can_admin ? (filters.status ?? filters.filter) : "published") ??
      "published",
  )
  const currentSort = filters.sort ?? "recent"
  const [allInterviews, setAllInterviews] = useState(interviews)
  const [isLoading, setIsLoading] = useState(false)
  const [isRequesting, setIsRequesting] = useState(false)

  // Sync local state with props
  useEffect(() => {
    if (isRequesting) return

    if (interviews.length > 0) {
      setAllInterviews((prevInterviews) => {
        const urlParams = new URLSearchParams(window.location.search)
        const currentOffset = parseInt(urlParams.get("offset") ?? "0")
        const isLoadMore = currentOffset > 0

        if (!isLoadMore) {
          return interviews
        } else {
          const newInterviews = interviews.filter(
            (newInterview) =>
              !prevInterviews.some(
                (existingInterview) => existingInterview.id === newInterview.id,
              ),
          )
          return [...prevInterviews, ...newInterviews]
        }
      })
      setIsLoading(false)
    } else if (interviews.length === 0 && !isLoading) {
      setAllInterviews([])
    }
  }, [interviews, isLoading, isRequesting])

  const doNavigate = (
    params: Record<string, string | number | boolean | null | undefined>,
  ) => {
    if (isRequesting) return

    setIsRequesting(true)
    setIsLoading(true)
    router.get(interviewsPath(), params, {
      preserveScroll: true,
      preserveState: true,
      only: ["interviews", "filters", "can_create", "can_admin", "pagination"],
      onFinish: () => {
        setIsRequesting(false)
        setIsLoading(false)
      },
    })
  }

  const handleFilterChange = (key: string, value: string) => {
    // Reset list and offset when changing filters
    setAllInterviews([])
    doNavigate({
      ...filters,
      [key]: value === "" ? undefined : value,
      offset: 0,
    })
  }

  const handleLoadMore = () => {
    if (isLoading || !pagination?.has_more || isRequesting) return

    setIsLoading(true)
    setIsRequesting(true)
    const nextOffset = pagination.current_offset + pagination.limit

    router.get(
      interviewsPath(),
      {
        ...filters,
        offset: nextOffset,
      },
      {
        preserveState: true,
        preserveScroll: true,
        only: ["interviews", "pagination"],
        onFinish: () => {
          setIsLoading(false)
          setIsRequesting(false)
        },
      },
    )
  }

  return (
    <AppLayout>
      <Head title="Entrevistas" />

      <div className="bg-background min-h-screen">
        <div className="mx-auto max-w-6xl space-y-3 md:p-6">
          <div className="flex flex-col">
            <div className="flex items-center justify-center gap-4">
              <h1 className="text-foreground flex items-center gap-2 text-[21px] font-bold tracking-[0.21px] md:text-3xl">
                <span className="bg-card flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-full p-1">
                  <img
                    src={EmoMic}
                    alt="Mic"
                    className="h-full w-full object-contain"
                  />
                </span>
                Entrevistas
              </h1>
              {can_create && (
                <Button
                  variant="default"
                  onClick={() => router.visit(interviewsNewPath())}
                  className="flex flex-shrink-0 items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline">Nueva entrevista</span>
                </Button>
              )}
            </div>
            <p className="mx-10 mt-[8px] text-center text-[13px] font-normal tracking-[0.2px] text-balance">
              Historias y perfiles de la comunidad.
            </p>
          </div>

          {/* Stats removed per requirement */}

          {/* Sort tabs */}
          <Tabs
            value={currentSort}
            onValueChange={(v) => handleFilterChange("sort", v)}
          >
            <div className="flex justify-center px-1">
              <TabsList>
                <TabsTrigger value="recent" disabled={isRequesting}>
                  Recientes
                </TabsTrigger>
                <TabsTrigger value="most_viewed" disabled={isRequesting}>
                  Mas Vistas
                </TabsTrigger>
                <TabsTrigger value="most_liked" disabled={isRequesting}>
                  Mas Likes
                </TabsTrigger>
              </TabsList>
            </div>
          </Tabs>

          {/* Admin status filter tabs */}
          {can_admin && (
            <Tabs
              value={currentStatus}
              onValueChange={(v) => handleFilterChange("status", v)}
              className="mt-4"
            >
              <div className="flex justify-center px-1">
                <TabsList className="inline-flex h-auto flex-wrap">
                  <TabsTrigger
                    value="published"
                    className="flex items-center gap-2 text-[13px] font-normal tracking-[0.26px] text-white data-[state=active]:font-bold"
                    disabled={isRequesting}
                  >
                    Publicadas
                  </TabsTrigger>
                  <TabsTrigger
                    value="draft"
                    className="flex items-center gap-2 text-[13px] font-normal tracking-[0.26px] text-white data-[state=active]:font-bold"
                    disabled={isRequesting}
                  >
                    Borradores
                  </TabsTrigger>
                  <TabsTrigger
                    value="archived"
                    className="flex items-center gap-2 text-[13px] font-normal tracking-[0.26px] text-white data-[state=active]:font-bold"
                    disabled={isRequesting}
                  >
                    Archivadas
                  </TabsTrigger>
                  <TabsTrigger
                    value="all"
                    className="flex items-center gap-2 text-[13px] font-normal tracking-[0.26px] text-white data-[state=active]:font-bold"
                    disabled={isRequesting}
                  >
                    Todas
                  </TabsTrigger>
                </TabsList>
              </div>
            </Tabs>
          )}

          {/* Content area */}
          <div className="mt-6 space-y-6">
            {/* Interviews Grid */}
            {allInterviews.length > 0 ? (
              <div className="grid grid-cols-2 gap-3 px-3 pb-2">
                {allInterviews.map((it: InterviewItem) => (
                  <InterviewCard key={it.id} interview={it} />
                ))}
              </div>
            ) : (
              <div
                className="mt-[20px] flex h-[245px] flex-col items-center justify-center text-center"
                style={{ background: "var(--gradient-highlight)" }}
              >
                <div className="mb-[5px] flex h-[56px] w-[56px] items-center justify-center">
                  <img src={EmoNeutral} alt="Sin contenido" />
                </div>
                <h3 className="text-center text-[18px] font-semibold tracking-[0px] sm:text-xl">
                  No se encontraron entrevistas
                </h3>
              </div>
            )}

            {/* Load More Button */}
            {allInterviews.length > 0 && pagination?.has_more && (
              <div className="text-center">
                <Button
                  variant="ghost"
                  onClick={handleLoadMore}
                  disabled={isLoading}
                  className="text-primary hover:bg-muted"
                >
                  {isLoading ? "Cargando..." : "Ver más entrevistas"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
